// Report is re-exported by @flareapp/js; importing it from @flareapp/core would
// reach into a transitive dependency we do not declare.
import { flare, type Report } from "@flareapp/js";

// A benign browser warning fired during normal layout, not a fault.
const isResizeObserverNoise = (report: Report): boolean =>
  (report.message ?? "").includes("ResizeObserver loop");

// The visitor has a tab open on a bundle a deploy has since replaced, so a lazy
// chunk 404s. Reporting these produces a spike on every deploy that correlates
// with no bug. Next's router recovers from these on its own.
const STALE_BUNDLE_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "error loading dynamically imported module",
  "ChunkLoadError",
  "Loading chunk",
];

export const isStaleBundleError = (report: Report): boolean => {
  const haystack = `${report.exceptionClass ?? ""} ${report.message ?? ""}`;

  return STALE_BUNDLE_PATTERNS.some((pattern) => haystack.includes(pattern));
};

/**
 * Reports that must never reach Flare. Kept as a pure predicate over the report
 * shape so it can be tested without standing up the client.
 *
 * Browser-extension frames are deliberately absent: the client filters those
 * itself via the reportBrowserExtensionErrors config flag.
 */
export const isNoise = (report: Report): boolean =>
  isResizeObserverNoise(report) || isStaleBundleError(report);

// The key the @flareapp/js browser collector fills from document.cookie. Its
// urlDenylist only redacts URLs, never the cookie object, so without this the
// collector ships tracking and consent cookies (PostHog, GA client ids, which
// are personal data) to a third-party processor on every report. Stripped
// before the report leaves the browser.
const SENSITIVE_ATTRIBUTES = ["http.request.cookies"];

// Cookies are not the only identifier a report carries. The collector also
// records url.full, url.query, the entry point and the referrer, and the SDK
// only redacts those against a generic keyword list (password, token, session
// and friends) that knows nothing about our own parameters. A visitor arriving
// on a referral link or an ad click would otherwise ship that identifier to
// Flare with every error. Merged with the SDK default rather than replacing
// it, so its keywords still apply.
// `^rs?$` is the referral code and its share surface, and the anchors are the
// whole point: `r` unanchored would match `referral`, `gbraid`, `utm_source`
// and every ordinary param with an r in it, redacting the URL into
// uselessness. `referral` stays as its own alternative because the redaction
// should outlive the 2026-09-03 rename, and because other surfaces may still
// send it.
export const URL_PARAM_DENYLIST =
  /^rs?$|referral|gclid|gbraid|wbraid|fbclid|ttclid|msclkid|utm_[a-z]+/i;

export const stripSensitiveAttributes = (report: Report): Report => {
  if (!report.attributes) {
    return report;
  }

  for (const key of SENSITIVE_ATTRIBUTES) {
    delete report.attributes[key];
  }

  return report;
};

let initialised = false;

export const isFlareInitialised = (): boolean => initialised;

/**
 * Without a key flare.light() is never called and the client silently discards
 * every report, which is what we want locally and in preview builds.
 *
 * The version must match the version the Next plugin uploaded source maps
 * under, otherwise traces stay minified.
 */
export const initFlare = (
  key: string | undefined,
  options: { stage?: string; release?: string } = {},
): void => {
  if (initialised || !key) {
    return;
  }

  initialised = true;

  flare.light(key);

  flare.configure({
    ...(options.stage ? { stage: options.stage } : {}),
    ...(options.release ? { version: options.release } : {}),
    // A visitor's own extension crashing inside our page. We cannot fix it and
    // it says nothing about our code. This is a public marketing site, so the
    // extension surface is far wider than on an authenticated app.
    reportBrowserExtensionErrors: false,
    urlDenylist: URL_PARAM_DENYLIST,
    // Load-bearing, not decoration: the SDK's log pipeline collects the same
    // browser context (cookies included) but flushes straight to the logs
    // endpoint without ever calling beforeSubmit, so the scrubber below cannot
    // reach it. This already defaults to false; pinning it keeps a future
    // config change from quietly opening an unscrubbed path.
    enableLogs: false,
    beforeSubmit: (report) =>
      isNoise(report) ? false : stripSensitiveAttributes(report),
  });
};

/**
 * Report an error the application has caught. Flare's global handlers never see
 * an error that a catch block or a React error boundary swallows, so those must
 * call this or the failure stays invisible.
 */
export const reportError = (
  error: unknown,
  attributes?: Record<string, string | number | boolean>,
): void => {
  if (!initialised) {
    return;
  }

  flare.reportSilently(
    error instanceof Error ? error : new Error(String(error)),
    attributes,
  );
};
